import { useState, useCallback, useEffect } from 'react';

// 초기 폼 데이터
const INITIAL_FORM_DATA = {
  title: '',
  username: '',
  password: '',
  website: '',
  category: '',
  tags: [],
  notes: ''
};

/**
 * 패스워드 폼 로직을 관리하는 커스텀 훅 (편집 모드 지원)
 * @param {Function} onSubmit - 폼 제출 핸들러
 * @param {Function} onCancel - 취소 핸들러
 * @param {Object} editingPassword - 편집할 패스워드 데이터 (편집 모드시)
 * @returns {Object} 폼 상태와 핸들러들
 */
export function usePasswordForm(onSubmit, onCancel, editingPassword = null) {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);

  /**
   * 편집할 패스워드 데이터로 폼 초기화
   */
  useEffect(() => {
    if (editingPassword) {
      setIsEditMode(true);
      setFormData({
        title: editingPassword.title || '',
        username: editingPassword.username || '',
        password: editingPassword.password || '',
        website: editingPassword.website || '',
        category: editingPassword.category || '',
        tags: editingPassword.tags || [],
        notes: editingPassword.notes || ''
      });
    } else {
      setIsEditMode(false);
      setFormData(INITIAL_FORM_DATA);
    }
    
    // 에러 상태 초기화
    setErrors({});
  }, [editingPassword]);

  /**
   * 폼 입력 핸들러
   */
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 에러 상태 초기화
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  }, [errors]);

  /**
   * 태그 변경 핸들러
   */
  const handleTagsChange = useCallback((newTags) => {
    setFormData(prev => ({
      ...prev,
      tags: newTags
    }));
  }, []);

  /**
   * 폼 유효성 검사
   */
  const validateForm = useCallback(() => {
    const newErrors = {};
    
    // 제목 검증
    if (!formData.title.trim()) {
      newErrors.title = '제목을 입력해주세요';
    } else if (formData.title.trim().length < 2) {
      newErrors.title = '제목은 최소 2자 이상이어야 합니다';
    } else if (formData.title.trim().length > 100) {
      newErrors.title = '제목은 100자를 초과할 수 없습니다';
    }
    
    // 사용자명 검증
    if (!formData.username.trim()) {
      newErrors.username = '사용자명을 입력해주세요';
    } else if (formData.username.trim().length > 200) {
      newErrors.username = '사용자명은 200자를 초과할 수 없습니다';
    }
    
    // 패스워드 검증
    if (!formData.password.trim()) {
      newErrors.password = '패스워드를 입력해주세요';
    } else if (formData.password.length < 1) {
      newErrors.password = '패스워드를 입력해주세요';
    } else if (formData.password.length > 500) {
      newErrors.password = '패스워드가 너무 깁니다';
    }
    
    // 웹사이트 URL 검증 (선택사항, 더 유연한 검증)
    if (formData.website && formData.website.trim()) {
      const websiteValue = formData.website.trim();
      // URL 형식 또는 도메인 형식 허용
      const urlPattern = /^(https?:\/\/)?([\w\-]+(\.[\w\-]+)+)(\/.*)?$/i;
      if (!urlPattern.test(websiteValue)) {
        newErrors.website = '올바른 웹사이트 주소를 입력해주세요 (예: google.com 또는 https://google.com)';
      }
    }
    
    // 메모 길이 검증
    if (formData.notes && formData.notes.length > 1000) {
      newErrors.notes = '메모는 1000자를 초과할 수 없습니다';
    }
    
    // 태그 개수 검증
    if (formData.tags && formData.tags.length > 10) {
      newErrors.tags = '태그는 최대 10개까지 추가할 수 있습니다';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  /**
   * 폼 제출 핸들러
   */
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    
    console.log('🚀 폼 제출 시작 - formData:', formData);
    
    if (!validateForm()) {
      console.log('❌ 폼 검증 실패');
      return;
    }
    
    // 폼 데이터 정리
    const cleanedData = {
      ...formData,
      title: formData.title.trim(),
      username: formData.username.trim(),
      password: formData.password,
      website: formData.website?.trim() || '',
      category: formData.category || '',
      tags: formData.tags || [],
      notes: formData.notes?.trim() || ''
    };
    
    console.log('✅ 정리된 데이터:', cleanedData);
    
    onSubmit(cleanedData);
    
    // 편집 모드가 아닐 때만 폼 초기화
    if (!isEditMode) {
      setFormData(INITIAL_FORM_DATA);
      setErrors({});
    }
  }, [formData, validateForm, onSubmit, isEditMode]);

  /**
   * 폼 취소 핸들러
   */
  const handleCancel = useCallback(() => {
    // 편집 모드에서는 원래 데이터로 복원, 새 추가 모드에서는 초기화
    if (isEditMode && editingPassword) {
      setFormData({
        title: editingPassword.title || '',
        username: editingPassword.username || '',
        password: editingPassword.password || '',
        website: editingPassword.website || '',
        category: editingPassword.category || '',
        tags: editingPassword.tags || [],
        notes: editingPassword.notes || ''
      });
    } else {
      setFormData(INITIAL_FORM_DATA);
    }
    
    setErrors({});
    onCancel();
  }, [onCancel, isEditMode, editingPassword]);

  /**
   * 폼 리셋 함수 (외부에서 호출 가능)
   */
  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setErrors({});
    setIsEditMode(false);
    
    // DOM 요소들도 강제로 초기화 (브라우저 자동완성 방지)
    setTimeout(() => {
      const formInputs = document.querySelectorAll('.password-form input, .password-form textarea, .password-form select');
      formInputs.forEach(input => {
        if (input.name === 'title' || input.name === 'username' || input.name === 'password' || 
            input.name === 'website' || input.name === 'notes') {
          input.value = '';
        }
        if (input.name === 'category') {
          input.selectedIndex = 0;
        }
      });
    }, 50);
  }, []);

  /**
   * 특정 필드 에러 설정 (외부에서 호출 가능)
   */
  const setFieldError = useCallback((fieldName, errorMessage) => {
    setErrors(prev => ({
      ...prev,
      [fieldName]: errorMessage
    }));
  }, []);

  /**
   * 폼 데이터 부분 업데이트 (외부에서 호출 가능)
   */
  const updateFormData = useCallback((updates) => {
    setFormData(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  return {
    formData,
    errors,
    isEditMode,
    handleInputChange,
    handleTagsChange,
    handleSubmit,
    handleCancel,
    resetForm,
    setFieldError,
    updateFormData,
    validateForm
  };
}

export default usePasswordForm;